import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Grid,
    Paper,
    Tabs,
    Tab,
    Divider
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    AddPhotoAlternate as AddPhotoIcon,
    ArrowUpward as ArrowUpIcon,
    ArrowDownward as ArrowDownIcon,
    Add as AddIcon,
    ImageNotSupported as NoImageIcon
} from '@mui/icons-material';
import { Location, LocationPhoto } from '../types/Location';
import { db, storage } from '../firebase';
import { collection, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';

interface LocationDialogProps {
    open: boolean;
    onClose: () => void;
    locationToEdit?: Location | null;
    onSave?: () => void;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
        </div>
    );
}

export const LocationDialog: React.FC<LocationDialogProps> = ({
    open,
    onClose,
    locationToEdit,
    onSave
}) => {
    const [tabValue, setTabValue] = useState(0);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [maxPlayers, setMaxPlayers] = useState<number | ''>('');
    const [adminNotes, setAdminNotes] = useState('');
    const [googleMapsUrl, setGoogleMapsUrl] = useState('');
    const [coverPhotoUrl, setCoverPhotoUrl] = useState('');
    const [gallery, setGallery] = useState<LocationPhoto[]>([]);

    // Legacy support to display old photos if any, to allow migration
    const [legacyPhotoUrls, setLegacyPhotoUrls] = useState<string[]>([]);

    const [uploading, setUploading] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [photosToDelete, setPhotosToDelete] = useState<string[]>([]);

    const coverInputRef = useRef<HTMLInputElement>(null);
    const galleryInputRef = useRef<HTMLInputElement>(null); // Pour l'ajout en masse (legacy ou si besoin)
    const stepPhotoInputRef = useRef<{ [key: number]: HTMLInputElement | null }>({});

    useEffect(() => {
        if (locationToEdit) {
            setName(locationToEdit.name);
            setDescription(locationToEdit.description || '');
            setMaxPlayers(locationToEdit.maxPlayers || '');
            setAdminNotes(locationToEdit.adminNotes || '');
            setGoogleMapsUrl(locationToEdit.googleMapsUrl);
            setCoverPhotoUrl(locationToEdit.coverPhotoUrl || '');
            setGallery(locationToEdit.gallery || []);
            setLegacyPhotoUrls(locationToEdit.photoUrls || []);
        } else {
            resetForm();
        }
    }, [locationToEdit, open]);

    const resetForm = () => {
        setName('');
        setDescription('');
        setMaxPlayers('');
        setAdminNotes('');
        setGoogleMapsUrl('');
        setCoverPhotoUrl('');
        setGallery([]);
        setLegacyPhotoUrls([]);
        setErrors({});
        setPhotosToDelete([]);
        setTabValue(0);
    };

    const validate = () => {
        const newErrors: { [key: string]: string } = {};
        if (!name.trim()) newErrors.name = 'Le nom est requis';
        if (!googleMapsUrl.trim()) newErrors.googleMapsUrl = 'Le lien Google Maps est requis';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;

        try {
            // Suppression des photos marquées
            if (photosToDelete.length > 0) {
                await Promise.all(photosToDelete.map(async (url) => {
                    try {
                        const photoRef = ref(storage, url);
                        await deleteObject(photoRef);
                    } catch (err) {
                        console.error(`Erreur lors de la suppression du fichier ${url}:`, err);
                        // On continue même si erreur (fichier peut-être déjà manquant)
                    }
                }));
            }

            const locationData = {
                name,
                description,
                maxPlayers: maxPlayers === '' ? null : Number(maxPlayers),
                adminNotes,
                googleMapsUrl,
                coverPhotoUrl,
                gallery,
                photoUrls: legacyPhotoUrls, // Keep legacy urls for now
                updatedAt: serverTimestamp()
            };

            if (locationToEdit && locationToEdit.id) {
                await updateDoc(doc(db, 'locations', locationToEdit.id), locationData);
            } else {
                await addDoc(collection(db, 'locations'), {
                    ...locationData,
                    createdAt: serverTimestamp()
                });
            }

            if (onSave) onSave();
            onClose();
        } catch (error) {
            console.error("Erreur lors de la sauvegarde du lieu:", error);
            alert("Une erreur est survenue lors de la sauvegarde.");
        }
    };

    const compressImage = async (file: File): Promise<{ blob: Blob; url: string }> => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                const MAX_SIZE = 1000;
                let width = img.width;
                let height = img.height;

                if (width > MAX_SIZE || height > MAX_SIZE) {
                    if (width > height) {
                        height = Math.round(height * (MAX_SIZE / width));
                        width = MAX_SIZE;
                    } else {
                        width = Math.round(width * (MAX_SIZE / height));
                        height = MAX_SIZE;
                    }
                }

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (!ctx) return reject('Canvas error');

                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(img, 0, 0, width, height);

                let quality = 0.82;
                const TARGET_SIZE = 250 * 1024; // 250ko

                const tryCompress = () => {
                    canvas.toBlob(blob => {
                        if (!blob) return reject('Blob error');
                        if (blob.size <= TARGET_SIZE || quality <= 0.5) {
                            const url = URL.createObjectURL(blob);
                            resolve({ blob, url });
                        } else {
                            quality -= 0.05;
                            tryCompress();
                        }
                    }, 'image/jpeg', quality);
                };
                tryCompress();
            };
            img.onerror = reject;
            img.src = URL.createObjectURL(file);
        });
    };

    const uploadImage = async (file: File): Promise<string> => {
        // Compression
        let fileToUpload: Blob = file;
        try {
            const { blob } = await compressImage(file);
            fileToUpload = blob;
        } catch (err) {
            console.error("Erreur de compression, utilisation de l'original:", err);
        }

        const fileExtension = 'jpg';
        const fileName = `location_${Date.now()}_${uuidv4().substring(0, 8)}.${fileExtension}`;
        const storagePath = `photos/locations_images/${fileName}`;
        const storageRef = ref(storage, storagePath);

        const fileToUploadTyped = new File([fileToUpload], fileName, { type: 'image/jpeg' });
        const uploadTask = await uploadBytesResumable(storageRef, fileToUploadTyped);
        return await getDownloadURL(uploadTask.ref);
    };

    const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        // Mark old cover for deletion if exists
        if (coverPhotoUrl) {
            setPhotosToDelete(prev => [...prev, coverPhotoUrl]);
        }

        setUploading(true);
        try {
            const url = await uploadImage(files[0]);
            setCoverPhotoUrl(url);
        } catch (error: any) {
            const errorMessage = error?.message || error?.code || "Erreur inconnue";
            alert(`Erreur upload cover: ${errorMessage}`);
        } finally {
            setUploading(false);
            if (coverInputRef.current) coverInputRef.current.value = '';
        }
    };

    const handleStepPhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        // Mark old photo for deletion if exists
        const currentPhotoUrl = gallery[index].url;
        if (currentPhotoUrl) {
            setPhotosToDelete(prev => [...prev, currentPhotoUrl]);
        }

        setUploading(true);
        try {
            const url = await uploadImage(files[0]);
            const newGallery = [...gallery];
            newGallery[index].url = url;
            setGallery(newGallery);
        } catch (error: any) {
            const errorMessage = error?.message || error?.code || "Erreur inconnue";
            alert(`Erreur upload étape: ${errorMessage}`);
        } finally {
            setUploading(false);
            if (stepPhotoInputRef.current[index]) {
                stepPhotoInputRef.current[index]!.value = '';
            }
        }
    };

    // Ajoute une étape VIDE (pas de photo)
    const handleAddStep = () => {
        setGallery(prev => [...prev, { description: '' }]);
    };

    const movePhoto = (index: number, direction: 'up' | 'down') => {
        const newGallery = [...gallery];
        if (direction === 'up' && index > 0) {
            [newGallery[index], newGallery[index - 1]] = [newGallery[index - 1], newGallery[index]];
        } else if (direction === 'down' && index < newGallery.length - 1) {
            [newGallery[index], newGallery[index + 1]] = [newGallery[index + 1], newGallery[index]];
        }
        setGallery(newGallery);
    };

    const deleteStep = (index: number) => {
        const photoUrl = gallery[index].url;
        if (photoUrl) {
            setPhotosToDelete(prev => [...prev, photoUrl]);
        }
        setGallery(prev => prev.filter((_, i) => i !== index));
    };

    const removeStepPhoto = (index: number) => {
        const photoUrl = gallery[index].url;
        if (photoUrl) {
            setPhotosToDelete(prev => [...prev, photoUrl]);
        }
        const newGallery = [...gallery];
        delete newGallery[index].url;
        setGallery(newGallery);
    };

    const updatePhotoDescription = (index: number, desc: string) => {
        const newGallery = [...gallery];
        newGallery[index].description = desc;
        setGallery(newGallery);
    };

    const removeCoverPhoto = () => {
        if (coverPhotoUrl) {
            setPhotosToDelete(prev => [...prev, coverPhotoUrl]);
            setCoverPhotoUrl('');
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {locationToEdit ? 'Modifier le lieu' : 'Ajouter un lieu'}
            </DialogTitle>
            <DialogContent dividers>
                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} centered sx={{ mb: 2 }}>
                    <Tab label="Général" />
                    <Tab label="Photos" />
                </Tabs>

                {/* TAB 1: GENERAL */}
                <TabPanel value={tabValue} index={0}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Nom du lieu"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            error={!!errors.name}
                            helperText={errors.name}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Lien Google Maps"
                            value={googleMapsUrl}
                            onChange={(e) => setGoogleMapsUrl(e.target.value)}
                            error={!!errors.googleMapsUrl}
                            helperText={errors.googleMapsUrl}
                            placeholder="https://maps.google.com/..."
                            fullWidth
                            required
                        />
                        <TextField
                            label="Kapla (Nombre max de joueurs)"
                            type="number"
                            value={maxPlayers}
                            onChange={(e) => setMaxPlayers(e.target.value === '' ? '' : Number(e.target.value))}
                            fullWidth
                            InputProps={{ inputProps: { min: 0 } }}
                        />
                        <TextField
                            label="Description publique"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            multiline
                            rows={3}
                            fullWidth
                        />
                        <TextField
                            label="Note Technique (Visible uniquement par les admins)"
                            value={adminNotes}
                            onChange={(e) => setAdminNotes(e.target.value)}
                            multiline
                            rows={3}
                            fullWidth
                            color="warning"
                            placeholder="Codes d'accès, contact gardien, procédures spécifiques..."
                            sx={{ bgcolor: 'rgba(255, 152, 0, 0.05)' }}
                        />
                    </Box>
                </TabPanel>

                {/* TAB 2: PHOTOS */}
                <TabPanel value={tabValue} index={1}>
                    <Typography variant="h6" gutterBottom>Photo de couverture</Typography>
                    <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                        {coverPhotoUrl ? (
                            <Paper sx={{ position: 'relative', width: 200, height: 120, backgroundImage: `url(${coverPhotoUrl})`, backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: 1 }}>
                                <IconButton
                                    size="small"
                                    sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(0,0,0,0.6)' }}
                                    onClick={removeCoverPhoto}
                                >
                                    <DeleteIcon fontSize="small" color="error" />
                                </IconButton>
                            </Paper>
                        ) : (
                            <Box sx={{ width: 200, height: 120, bgcolor: 'grey.300', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">Aucune vignette</Typography>
                            </Box>
                        )}
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
                            disabled={uploading}
                        >
                            Changer couverture
                            <input type="file" hidden accept="image/*" onChange={handleCoverUpload} ref={coverInputRef} />
                        </Button>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Galerie / Instructions d'accès</Typography>
                        <Button
                            variant="contained"
                            onClick={handleAddStep}
                            startIcon={<AddIcon />}
                            disabled={uploading}
                        >
                            Ajouter une étape
                        </Button>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {gallery.map((step, index) => (
                            <Paper key={index} sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                {/* Controls */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt: 1 }}>
                                    <IconButton size="small" onClick={() => movePhoto(index, 'up')} disabled={index === 0}>
                                        <ArrowUpIcon />
                                    </IconButton>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{index + 1}</Typography>
                                    <IconButton size="small" onClick={() => movePhoto(index, 'down')} disabled={index === gallery.length - 1}>
                                        <ArrowDownIcon />
                                    </IconButton>
                                </Box>

                                {/* Photo Area */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: 140, flexShrink: 0 }}>
                                    {step.url ? (
                                        <Box sx={{ position: 'relative', width: 140, height: 100 }}>
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    backgroundImage: `url(${step.url})`,
                                                    backgroundSize: 'cover',
                                                    backgroundPosition: 'center',
                                                    borderRadius: 1,
                                                    border: '1px solid #ddd'
                                                }}
                                            />

                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => removeStepPhoto(index)}
                                                sx={{
                                                    position: 'absolute',
                                                    top: -8,
                                                    right: -8,
                                                    bgcolor: 'background.paper',
                                                    border: '1px solid #ccc',
                                                    '&:hover': { bgcolor: '#ffebee' }
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            sx={{
                                                width: 140,
                                                height: 100,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.5,
                                                textTransform: 'none',
                                                borderStyle: 'dashed'
                                            }}
                                            disabled={uploading}
                                        >
                                            <AddPhotoIcon color="action" />
                                            <Typography variant="caption" color="text.secondary">Ajouter photo</Typography>
                                            <input
                                                type="file"
                                                hidden
                                                accept="image/*"
                                                onChange={(e) => handleStepPhotoUpload(e, index)}
                                                ref={(el) => { stepPhotoInputRef.current[index] = el; }}
                                            />
                                        </Button>
                                    )}
                                </Box>

                                {/* Text Area */}
                                <TextField
                                    fullWidth
                                    label={`Instruction étape ${index + 1}`}
                                    value={step.description || ''}
                                    onChange={(e) => updatePhotoDescription(index, e.target.value)}
                                    placeholder="Ex: Garez-vous sur ce parking..."
                                    multiline
                                    rows={3}
                                />

                                {/* Delete Step */}
                                <IconButton color="error" onClick={() => deleteStep(index)} sx={{ mt: 1 }}>
                                    <DeleteIcon />
                                </IconButton>
                            </Paper>
                        ))}
                    </Box>

                    {gallery.length === 0 && (
                        <Typography color="text.secondary" align="center" sx={{ my: 4 }}>
                            Aucune étape ajoutée. Cliquez sur "Ajouter une étape" pour créer votre guide.
                        </Typography>
                    )}
                </TabPanel>

            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">
                    Annuler
                </Button>
                <Button onClick={handleSave} variant="contained" color="primary">
                    Enregistrer
                </Button>
            </DialogActions>
        </Dialog>
    );
};
